import React from 'react';
import {
  Trash2,
  Filter,
  Settings,
  Download,
  Info,
  AlertTriangle,
  XCircle,
  Bug,
  FileText,
} from 'lucide-react';
import { useConsoleStore } from '../../store/consoleStore';
import { cn } from '../../utils/cn';

export const ConsoleToolbar: React.FC = () => {
  const filter = useConsoleStore((state) => state.filter);
  const isSettingsOpen = useConsoleStore((state) => state.isSettingsOpen);
  const infoCount = useConsoleStore((state) => state.infoCount);
  const warningCount = useConsoleStore((state) => state.warningCount);
  const errorCount = useConsoleStore((state) => state.errorCount);
  const debugCount = useConsoleStore((state) => state.debugCount);
  const setSearchText = useConsoleStore((state) => state.setSearchText);
  const toggleMessageType = useConsoleStore((state) => state.toggleMessageType);
  const clear = useConsoleStore((state) => state.clear);
  const toggleSettings = useConsoleStore((state) => state.toggleSettings);
  const exportLog = useConsoleStore((state) => state.exportLog);

  const handleExport = () => {
    const logContent = exportLog();
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `console-log-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 border-b border-zinc-700 bg-zinc-800 flex-shrink-0">
      {/* Clear button */}
      <button
        onClick={clear}
        className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
        title="Clear console (Ctrl+L)"
      >
        <Trash2 size={14} />
      </button>

      {/* Divider */}
      <div className="w-px h-4 bg-zinc-600" />

      {/* Search filter */}
      <div className="relative flex-1 max-w-xs">
        <Filter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={filter.searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Filter messages..."
          className="w-full pl-7 pr-2 py-1 text-xs bg-zinc-900 border border-zinc-600 rounded
            focus:border-blue-500 focus:outline-none text-zinc-300 placeholder:text-zinc-500"
        />
      </div>

      {/* Divider */}
      <div className="w-px h-4 bg-zinc-600" />

      {/* Message type filters */}
      <div className="flex items-center gap-1">
        {/* Errors */}
        <button
          onClick={() => toggleMessageType('error')}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
            filter.showErrors
              ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
              : 'bg-zinc-700/50 text-zinc-500 hover:bg-zinc-700'
          )}
          title="Toggle errors"
        >
          <XCircle size={12} />
          <span>{errorCount}</span>
        </button>

        {/* Warnings */}
        <button
          onClick={() => toggleMessageType('warning')}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
            filter.showWarnings
              ? 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50'
              : 'bg-zinc-700/50 text-zinc-500 hover:bg-zinc-700'
          )}
          title="Toggle warnings"
        >
          <AlertTriangle size={12} />
          <span>{warningCount}</span>
        </button>

        {/* Info */}
        <button
          onClick={() => toggleMessageType('info')}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
            filter.showInfo
              ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'
              : 'bg-zinc-700/50 text-zinc-500 hover:bg-zinc-700'
          )}
          title="Toggle info"
        >
          <Info size={12} />
          <span>{infoCount}</span>
        </button>

        {/* Debug (verbose) */}
        <button
          onClick={() => toggleMessageType('debug')}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
            filter.showDebug
              ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50'
              : 'bg-zinc-700/50 text-zinc-500 hover:bg-zinc-700'
          )}
          title="Toggle verbose/debug"
        >
          <Bug size={12} />
          <span>{debugCount}</span>
        </button>

        {/* Logs */}
        <button
          onClick={() => toggleMessageType('log')}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
            filter.showLogs
              ? 'bg-zinc-600/50 text-zinc-300 hover:bg-zinc-600'
              : 'bg-zinc-700/50 text-zinc-500 hover:bg-zinc-700'
          )}
          title="Toggle logs"
        >
          <FileText size={12} />
        </button>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Export button */}
      <button
        onClick={handleExport}
        className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
        title="Export log"
      >
        <Download size={14} />
      </button>

      {/* Settings button */}
      <button
        onClick={toggleSettings}
        className={cn(
          'p-1.5 rounded transition-colors',
          isSettingsOpen ? 'bg-zinc-600 text-zinc-200' : 'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200'
        )}
        title="Console settings"
      >
        <Settings size={14} />
      </button>
    </div>
  );
};
